import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { SiteHeader } from "@/components/layout/SiteHeader";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const LAST_UPDATED = "January 2025";

const Section = ({ title, children }) => (
  <div className="mt-12">
    <h2 className="font-serif-heading text-xl sm:text-2xl font-medium text-foreground">{title}</h2>
    <div className="mt-4 space-y-4 text-sm sm:text-base leading-[1.85] text-foreground/70">{children}</div>
  </div>
);

const PrivacyContent = () => (
  <>
    <Section title="1. What we collect">
      <p>
        When you request a free consultation, we collect the information you
        provide: your name, email address, phone number (if you choose to share
        it), and the details of your project. That's it — we don't buy data,
        track you across the web, or build advertising profiles.
      </p>
    </Section>
    <Section title="2. How we use it">
      <p>
        We use your information for one purpose: to respond to your enquiry and,
        if you continue with us, to deliver our education, training, and
        consulting services. We do not sell, rent, or share your personal
        information with third parties for marketing purposes — ever.
      </p>
    </Section>
    <Section title="3. Where it lives">
      <p>
        Consultation requests are stored securely in our systems and are
        accessible only to the small team that responds to them. We retain your
        information only as long as needed to serve you, or as required by law.
      </p>
    </Section>
    <Section title="4. Your rights">
      <p>
        You may request a copy of the personal information we hold about you,
        ask us to correct it, or ask us to delete it entirely. Write to{" "}
        <a href="mailto:info@pondresourcecentre.com" className="nav-underline text-[color:var(--apn-forest)]">
          info@pondresourcecentre.com
        </a>{" "}
        and we'll respond within 30 days.
      </p>
    </Section>
    <Section title="5. Questions">
      <p>
        Privacy questions deserve straight answers. Reach us any time at{" "}
        <a href="mailto:info@pondresourcecentre.com" className="nav-underline text-[color:var(--apn-forest)]">
          info@pondresourcecentre.com
        </a>
        .
      </p>
    </Section>
  </>
);

const TermsContent = () => (
  <>
    <Section title="1. Who we are">
      <p>
        Pond & Aquaculture Resource Centre provides independent education, training, and
        consulting for pond aquaculture in Alberta, Canada. We do not sell
        fish, equipment, or pond construction services, and nothing on this
        site is an offer to do so.
      </p>
    </Section>
    <Section title="2. Educational information and guidance">
      <p>
        Our content, programs, and consultations are educational. Every
        property, pond, and project is different; outcomes depend on factors
        beyond anyone's control, including weather, water, and your own
        decisions. We give honest, evidence-based guidance — we cannot
        guarantee results.
      </p>
    </Section>
    <Section title="3. Your responsibilities">
      <p>
        You are responsible for complying with all applicable laws and
        regulations, including licensing requirements for private fish culture
        in Alberta, and for decisions made about your own land and water. Our
        guidance supports your decisions. You remain responsible for
        evaluating your options and making final choices.
      </p>
    </Section>
    <Section title="4. Consultations">
      <p>
        The introductory consultation is free. Program and consulting fees are
        discussed and agreed upon in writing before any work begins.
      </p>
    </Section>
    <Section title="5. Limitation of liability">
      <p>
        To the maximum extent permitted by law, Pond & Aquaculture Resource Centre is not
        liable for indirect or consequential losses arising from the use of
        this website or our educational materials.
      </p>
    </Section>
    <Section title="6. Contact">
      <p>
        Questions about these terms? Write to{" "}
        <a href="mailto:info@pondresourcecentre.com" className="nav-underline text-[color:var(--apn-forest)]">
          info@pondresourcecentre.com
        </a>
        .
      </p>
    </Section>
  </>
);

const CookiesContent = () => {
  const [analytics, setAnalytics] = useState(false);
  const [functional, setFunctional] = useState(true);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("apn-cookie-prefs") || "{}");
      if (typeof saved.analytics === "boolean") setAnalytics(saved.analytics);
      if (typeof saved.functional === "boolean") setFunctional(saved.functional);
    } catch {
      /* ignore */
    }
  }, []);

  const save = () => {
    localStorage.setItem("apn-cookie-prefs", JSON.stringify({ analytics, functional }));
    toast.success("Preferences saved", {
      description: "Your cookie choices have been stored on this device.",
    });
  };

  const rows = [
    {
      id: "essential",
      title: "Essential",
      body: "Required for the site to function — things like remembering your form submission state. These can't be turned off.",
      control: <span className="font-mono-meta text-[10px] text-foreground/50">Always on</span>,
    },
    {
      id: "functional",
      title: "Functional",
      body: "Remember small preferences, like these cookie choices, so you don't have to set them twice.",
      control: (
        <Switch
          checked={functional}
          onCheckedChange={setFunctional}
          data-testid="cookie-functional-switch"
          aria-label="Toggle functional cookies"
        />
      ),
    },
    {
      id: "analytics",
      title: "Analytics",
      body: "Help us understand — anonymously — which parts of the site are useful. We currently use no third-party analytics; this setting is honoured if that ever changes.",
      control: (
        <Switch
          checked={analytics}
          onCheckedChange={setAnalytics}
          data-testid="cookie-analytics-switch"
          aria-label="Toggle analytics cookies"
        />
      ),
    },
  ];

  return (
    <>
      <Section title="Our approach to cookies">
        <p>
          This site is intentionally quiet. We use only the minimum necessary to
          make it work, and nothing that follows you around the internet.
          Adjust your preferences below. Your choices are stored on your device
          according to your selected settings.
        </p>
      </Section>

      <div className="mt-10">
        {rows.map((row) => (
          <div key={row.id} data-testid={`cookie-row-${row.id}`} className="hairline py-7 flex items-start justify-between gap-8">
            <div>
              <h3 className="font-serif-heading text-lg font-medium text-foreground">{row.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground/65 max-w-[56ch]">{row.body}</p>
            </div>
            <div className="pt-1 shrink-0">{row.control}</div>
          </div>
        ))}
        <div className="hairline" />
      </div>

      <Button
        onClick={save}
        data-testid="cookie-save-button"
        className="btn-lift mt-10 rounded-none bg-[color:var(--apn-forest)] text-[color:var(--apn-paper-2)] hover:bg-[color:var(--apn-forest-2)] h-11 px-7"
      >
        Save preferences
      </Button>
    </>
  );
};

const PAGES = {
  privacy: {
    kicker: "Legal",
    title: "Privacy policy",
    dek: "Plain language, like everything else we do. Here is what we collect, why, and what we will never do with it.",
    content: <PrivacyContent />,
  },
  terms: {
    kicker: "Legal",
    title: "Terms of service",
    dek: "The terms that explain how this website and our services are used.",
    content: <TermsContent />,
  },
  cookies: {
    kicker: "Legal",
    title: "Cookie preferences",
    dek: "We keep tracking to a bare minimum. Review and adjust what this site may store on your device.",
    content: <CookiesContent />,
  },
};

export default function LegalPage({ type }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [type]);

  const page = PAGES[type] || PAGES.privacy;

  return (
    <div className="App bg-background text-foreground min-h-screen flex flex-col">
      <SiteHeader />
      <main className="flex-1" data-testid={`legal-page-${type}`}>
        <div className="mx-auto w-full max-w-[820px] px-5 sm:px-8 py-20 sm:py-28">
          <Link
            to="/"
            data-testid="legal-back-link"
            className="nav-underline inline-flex items-center gap-2 font-mono-meta text-[10px] text-foreground/55 hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            Back to home
          </Link>

          <div className="mt-10 flex items-baseline gap-4">
            <span className="font-mono-meta text-xs text-[color:var(--apn-lake)]">{page.kicker}</span>
            <span className="h-px w-12 bg-[color:var(--apn-border)]" aria-hidden="true" />
            <span className="font-mono-meta text-xs text-foreground/50">Updated {LAST_UPDATED}</span>
          </div>

          <h1 className="mt-6 font-serif-heading font-medium text-4xl sm:text-5xl leading-[1.02] tracking-[-0.015em] text-foreground">
            {page.title}
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-foreground/60 max-w-[56ch]">{page.dek}</p>

          {page.content}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
